import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Loader2,
  Building2,
  User,
  Users,
  Tag,
  FileText,
  Landmark,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Combobox, ComboboxOption } from '@/components/ui/combobox';
import { CategorySelect } from '@/components/Financial/Shared/CategorySelect';
import { BankTransaction } from '@/hooks/useBankTransactions';
import { useCreateTransactionFromBank } from '@/hooks/useCreateTransactionFromBank';
import { useCustomers } from '@/hooks/useCustomers';
import { useCostCenters } from '@/hooks/useCostCenters';
import { cn } from '@/lib/utils';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

interface CreateTransactionFromBankDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: BankTransaction | null;
  onSuccess?: () => void;
}

export const CreateTransactionFromBankDialog = ({
  open,
  onOpenChange,
  transaction,
  onSuccess,
}: CreateTransactionFromBankDialogProps) => {
  // Estados do formulário
  const [transactionType, setTransactionType] = useState<'receivable' | 'payable'>('receivable');
  const [categoryId, setCategoryId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');

  // Hooks
  const { mutate: createTransaction, isPending } = useCreateTransactionFromBank();
  const { customers, isLoading: isLoadingCustomers } = useCustomers();
  const { centers: costCenters, isLoading: isLoadingCostCenters } = useCostCenters();

  // Reset form quando o dialog abre/fecha ou muda a transação
  useEffect(() => {
    if (open && transaction) {
      // Definir tipo baseado no tipo da transação bancária
      const type = transaction.transaction_type === 'credit' ? 'receivable' : 'payable';
      setTransactionType(type);
      setDescription(transaction.description);
      setCategoryId('');
      setCustomerId('');
      setSupplierId('');
      setCostCenterId('');
      setNotes('');
    }
  }, [open, transaction]);

  // Opções para comboboxes
  const customerOptions: ComboboxOption[] = customers.map(c => ({
    value: c.id,
    label: c.name,
    description: c.document_number || undefined,
  }));

  // Fornecedores são os mesmos customers (podem ser usados como fornecedores também)
  const supplierOptions: ComboboxOption[] = customers.map(c => ({
    value: c.id,
    label: c.name,
    description: c.document_number || undefined,
  }));

  const costCenterOptions: ComboboxOption[] = costCenters.map(cc => ({
    value: cc.id,
    label: cc.center_name,
    description: cc.description || undefined,
  }));

  const handleSubmit = () => {
    if (!transaction || !categoryId) return;

    // Validações específicas
    if (transactionType === 'receivable' && !customerId) {
      return;
    }
    if (transactionType === 'payable' && !supplierId) {
      return;
    }

    createTransaction(
      {
        bankTransaction: transaction,
        transactionType,
        categoryId,
        customerId: transactionType === 'receivable' ? customerId : undefined,
        supplierId: transactionType === 'payable' ? supplierId : undefined,
        costCenterId: costCenterId || undefined,
        description: description || undefined,
        notes: notes || undefined,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
      }
    );
  };

  if (!transaction) return null;

  const isCredit = transaction.transaction_type === 'credit';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Criar Lançamento a partir do Banco
          </DialogTitle>
          <DialogDescription>
            Crie um lançamento financeiro a partir desta transação bancária
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Card com dados da transação bancária */}
          <div className={cn(
            'p-4 rounded-lg border',
            isCredit ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'
          )}>
            <div className="flex items-start gap-3">
              <div className={cn(
                'p-2 rounded-full',
                isCredit ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
              )}>
                {isCredit ? (
                  <ArrowDownCircle className="h-5 w-5" />
                ) : (
                  <ArrowUpCircle className="h-5 w-5" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{transaction.description}</p>
                <div className="flex gap-2 text-xs text-muted-foreground mt-1">
                  <span>{format(new Date(`${transaction.transaction_date}T12:00:00`), 'dd/MM/yyyy', { locale: ptBR })}</span>
                  {transaction.counterpart_name && (
                    <span>• {transaction.counterpart_name}</span>
                  )}
                </div>
              </div>
              <p className={cn(
                'font-bold text-lg',
                isCredit ? 'text-green-600' : 'text-red-600'
              )}>
                {isCredit ? '+' : '-'}{formatCurrency(transaction.value)}
              </p>
            </div>
          </div>

          {/* Tipo de lançamento */}
          <div className="space-y-3">
            <Label>Tipo de Lançamento</Label>
            <RadioGroup
              value={transactionType}
              onValueChange={(v) => setTransactionType(v as 'receivable' | 'payable')}
              className="grid grid-cols-2 gap-4"
            >
              <div>
                <RadioGroupItem
                  value="receivable"
                  id="receivable"
                  className="peer sr-only"
                />
                <Label
                  htmlFor="receivable"
                  className={cn(
                    'flex items-center gap-2 p-4 border rounded-lg cursor-pointer transition-all',
                    'hover:bg-muted/50',
                    transactionType === 'receivable'
                      ? 'border-green-500 bg-green-500/5 text-green-700'
                      : 'border-border'
                  )}
                >
                  <ArrowDownCircle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Receita</p>
                    <p className="text-xs text-muted-foreground">Conta a Receber</p>
                  </div>
                </Label>
              </div>
              <div>
                <RadioGroupItem
                  value="payable"
                  id="payable"
                  className="peer sr-only"
                />
                <Label
                  htmlFor="payable"
                  className={cn(
                    'flex items-center gap-2 p-4 border rounded-lg cursor-pointer transition-all',
                    'hover:bg-muted/50',
                    transactionType === 'payable'
                      ? 'border-red-500 bg-red-500/5 text-red-700'
                      : 'border-border'
                  )}
                >
                  <ArrowUpCircle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Despesa</p>
                    <p className="text-xs text-muted-foreground">Conta a Pagar</p>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Categoria */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Categoria *
            </Label>
            <CategorySelect
              type={transactionType === 'receivable' ? 'income' : 'expense'}
              value={categoryId}
              onValueChange={setCategoryId}
            />
          </div>

          {/* Cliente (para receitas) ou Fornecedor (para despesas) */}
          {transactionType === 'receivable' ? (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Cliente *
              </Label>
              <Combobox
                options={customerOptions}
                value={customerId}
                onValueChange={setCustomerId}
                placeholder={isLoadingCustomers ? 'Carregando...' : 'Selecione o cliente'}
                searchPlaceholder="Buscar cliente..."
                emptyMessage="Nenhum cliente encontrado"
                disabled={isLoadingCustomers}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Fornecedor *
              </Label>
              <Combobox
                options={supplierOptions}
                value={supplierId}
                onValueChange={setSupplierId}
                placeholder={isLoadingCustomers ? 'Carregando...' : 'Selecione o fornecedor'}
                searchPlaceholder="Buscar fornecedor..."
                emptyMessage="Nenhum fornecedor encontrado"
                disabled={isLoadingCustomers}
              />
            </div>
          )}

          {/* Centro de Custo (opcional, principalmente para despesas) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Centro de Custo
            </Label>
            <Combobox
              options={costCenterOptions}
              value={costCenterId}
              onValueChange={setCostCenterId}
              placeholder={isLoadingCostCenters ? 'Carregando...' : 'Selecione o centro de custo (opcional)'}
              searchPlaceholder="Buscar centro de custo..."
              emptyMessage="Nenhum centro de custo encontrado"
              disabled={isLoadingCostCenters}
            />
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label>Descrição</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição do lançamento"
            />
          </div>

          {/* Observações */}
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observações adicionais..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending || !categoryId || (transactionType === 'receivable' ? !customerId : !supplierId)}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Criando...
              </>
            ) : (
              <>
                <Landmark className="h-4 w-4 mr-2" />
                Criar Lançamento
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
